// src/pages/Produtividade.js - VERSÃO REFORMULADA
import React, { useState, useEffect, useCallback } from 'react';
import { 
  Paper, Grid, Card, CardContent, Typography, Box,
  CircularProgress, Alert, TextField, Select, MenuItem,
  FormControl, InputLabel, Button, Divider, Autocomplete,
  Dialog, DialogTitle, DialogContent, DialogActions, List, ListItem, ListItemText
} from '@mui/material';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, 
  Tooltip as RechartsTooltip, ResponsiveContainer, Area, AreaChart
} from 'recharts';
import { 
  TrendingUp, Business, Today, Refresh
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';

const CORES = {
  primary: '#1976d2',
  secondary: '#dc004e', 
  success: '#2e7d32',
  warning: '#ed6c02',
  error: '#d32f2f',
  info: '#0288d1'
};

const Produtividade = () => {
  const { api } = useAuth();
  
  // Estados principais
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Estados de filtros
  const [filtros, setFiltros] = useState({
    dataInicio: '',
    dataFim: '',
    setor: '',
    funcionario: ''
  });
  const [funcionarioSelecionado, setFuncionarioSelecionado] = useState(null);
  
  // Estados de dados
  const [opcoesFiltro, setOpcoesFiltro] = useState({ setores: [], funcionarios: [] });
  const [graficos, setGraficos] = useState({
    formulasPorDia: [],
    tempoMedioSetores: [],
    producaoFuncionario: [],
    tempoMedioFormulas: []
  });
  const [cards, setCards] = useState({
    formulasHoje: 0,
    setores: [],
    setoresHoje: []
  });
  const [requisicoesHoje, setRequisicoesHoje] = useState(0);
  const [requisicoesHojePorSetor, setRequisicoesHojePorSetor] = useState([]);
  const [ultimaAtualizacao, setUltimaAtualizacao] = useState(null);
  const [integridadeContagem, setIntegridadeContagem] = useState(true);

  // Estados do modal de setor
  const [modalSetorOpen, setModalSetorOpen] = useState(false);
  const [modalSetorNome, setModalSetorNome] = useState('');
  const [modalSetorLoading, setModalSetorLoading] = useState(false);
  const [modalSetorFuncionarios, setModalSetorFuncionarios] = useState([]);

  // Função para verificar integridade da contagem
  const verificarIntegridadeContagem = (dados, requisicoesUnicas, setoresArray) => {
    const somaSetores = setoresArray.reduce((acc, setor) => acc + setor.quantidade, 0);
    return somaSetores === requisicoesUnicas.size;
  };

  // Carregar requisições do dia atual
  const carregarRequisicoesHoje = useCallback(async () => {
    try {
      const hoje = new Date().toISOString().split('T')[0];
      const res = await api.relatorioLeituras({
        dataInicio: hoje,
        dataFim: hoje
      });
      
      if (res.success) {
        const dados = res.dados || [];

        // Contar requisições únicas do dia
        const requisicoesUnicas = new Set(dados.map(item => item.codigo_requisicao));
        setRequisicoesHoje(requisicoesUnicas.size);

        // Agrupar por setor - usar setor_efetivo se disponível
        const setoresHoje = {};
        dados.forEach(item => {
          const setor = item.setor_efetivo || item.setor_leitura || item.funcionario_setor || 'Sem Setor';
          if (!setoresHoje[setor]) {
            setoresHoje[setor] = new Set();
          }
          setoresHoje[setor].add(item.codigo_requisicao);
        });

        // Converter para array ordenado
        const setoresArray = Object.entries(setoresHoje)
          .map(([setor, requisicoes]) => ({
            setor,
            quantidade: requisicoes.size
          }))
          .sort((a, b) => b.quantidade - a.quantidade);

        // Verificar integridade da contagem
        const integridadeOk = verificarIntegridadeContagem(dados, requisicoesUnicas, setoresArray);
        setIntegridadeContagem(integridadeOk);

        setRequisicoesHojePorSetor(setoresArray);
        setUltimaAtualizacao(new Date());
      }
    } catch (error) {
      console.error('Erro ao carregar requisições de hoje:', error);
    }
  }, [api]);

  // Carregar opções de filtros
  const carregarOpcoesFiltro = useCallback(async () => {
    try {
      const [resSetores, resFuncionarios] = await Promise.all([
        api.listarSetores(),
        api.listarFuncionarios()
      ]);
      
      setOpcoesFiltro({
        setores: resSetores.success ? resSetores.dados : [],
        funcionarios: resFuncionarios.success ? resFuncionarios.dados : []
      });
    } catch (error) {
      console.error('Erro ao carregar opções de filtro:', error);
    }
  }, [api]);

  // Carregar dados gerais da dashboard
  const carregarDadosGerais = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Obter primeiro e último dia do mês atual
      const hoje = new Date();
      const primeiroDiaMes = new Date(hoje.getFullYear(), hoje.getMonth(), 1);
      const ultimoDiaMes = new Date(hoje.getFullYear(), hoje.getMonth() + 1, 0);
      
      const dataInicio = primeiroDiaMes.toISOString().split('T')[0];
      const dataFim = ultimoDiaMes.toISOString().split('T')[0];
      
      // Carregar dados do mês atual
      const res = await api.relatorioLeituras({
        dataInicio,
        dataFim
      });
      
      if (res.success) {
        const dados = res.dados || [];
        
        // Processar dados para gráficos e cards
        processarDadosGerais(dados);
        
        // Se há filtros aplicados, carregar dados específicos
        if (funcionarioSelecionado) {
          await carregarDadosFuncionario();
        }
      } else {
        setError(res.message || 'Erro ao carregar dados');
      }
    } catch (error) {
      console.error('Erro ao carregar dados gerais:', error);
      setError('Erro ao carregar dados. Tente novamente.');
    } finally {
      setLoading(false);
    }
  }, [api, funcionarioSelecionado]);

  // Processar dados gerais para gráficos e cards
  const processarDadosGerais = (dados) => {
    const hoje = new Date().toISOString().split('T')[0];
    
    // Fórmulas por dia do mês atual
    const formulasPorDia = {};
    const setoresRequisicoes = {};
    const setoresHoje = {};
    let formulasHoje = 0;
    
    dados.forEach(item => {
      const dataItem = new Date(item.data).toISOString().split('T')[0];
      
      // Agrupar por dia
      if (!formulasPorDia[dataItem]) {
        formulasPorDia[dataItem] = new Set();
      }
      formulasPorDia[dataItem].add(item.codigo_requisicao);
      
      // Contar fórmulas de hoje
      if (dataItem === hoje) {
        formulasHoje++;
      }
      
      // Agrupar por setor (total do período)
      const setor = item.setor_efetivo || item.setor_leitura || item.funcionario_setor || 'Sem Setor';
      if (!setoresRequisicoes[setor]) {
        setoresRequisicoes[setor] = new Set();
      }
      setoresRequisicoes[setor].add(item.codigo_requisicao);
      
      // Agrupar por setor (apenas hoje)
      if (dataItem === hoje) {
        if (!setoresHoje[setor]) {
          setoresHoje[setor] = new Set();
        }
        setoresHoje[setor].add(item.codigo_requisicao);
      }
    });
    
    // Converter para array do gráfico
    const graficoPorDia = Object.entries(formulasPorDia)
      .map(([data, requisicoes]) => ({
        data: new Date(data).toLocaleDateString('pt-BR', { 
          day: '2-digit', 
          month: '2-digit' 
        }),
        dataCompleta: data,
        formulas: requisicoes.size
      }))
      .sort((a, b) => new Date(a.dataCompleta) - new Date(b.dataCompleta));
      
    // Converter setores para cards
    const setoresCards = Object.entries(setoresRequisicoes)
      .map(([setor, requisicoes]) => ({
        setor,
        quantidade: requisicoes.size
      }))
      .sort((a, b) => b.quantidade - a.quantidade);
      
    // Converter setores de hoje para cards
    const setoresCardsHoje = Object.entries(setoresHoje)
      .map(([setor, requisicoes]) => ({
        setor,
        quantidade: requisicoes.size
      }))
      .sort((a, b) => b.quantidade - a.quantidade);
    
    // Calcular tempo médio entre setores (simplificado)
    const tempoMedioSetores = calcularTempoMedioSetores(dados);
    
    setGraficos(prev => ({
      ...prev,
      formulasPorDia: graficoPorDia,
      tempoMedioSetores
    }));
    
    setCards({
      formulasHoje,
      setores: setoresCards,
      setoresHoje: setoresCardsHoje
    });
  };

  // Calcular tempo médio entre setores
  const calcularTempoMedioSetores = (dados) => {
    const transicoes = {};
    
    // Agrupar por requisição
    const requisicoesPorCodigo = {};
    dados.forEach(item => {
      const codigo = item.codigo_requisicao;
      if (!requisicoesPorCodigo[codigo]) {
        requisicoesPorCodigo[codigo] = [];
      }
      requisicoesPorCodigo[codigo].push(item);
    });
    
    // Calcular transições
    Object.values(requisicoesPorCodigo).forEach(requisicao => {
      const sortedReq = requisicao.sort((a, b) => new Date(a.data) - new Date(b.data));
      
      for (let i = 0; i < sortedReq.length - 1; i++) {
        const atual = sortedReq[i];
        const proximo = sortedReq[i + 1];
        
        const setorAtual = atual.setor_leitura || atual.funcionario_setor || 'Sem Setor';
        const setorProximo = proximo.setor_leitura || proximo.funcionario_setor || 'Sem Setor';
        
        if (setorAtual !== setorProximo) {
          const key = `${setorAtual} → ${setorProximo}`;
          const tempoMs = new Date(proximo.data) - new Date(atual.data);
          const tempoMin = Math.round(tempoMs / (1000 * 60));
          
          if (!transicoes[key]) {
            transicoes[key] = [];
          }
          transicoes[key].push(tempoMin);
        }
      }
    });
    
    // Calcular médias
    return Object.entries(transicoes)
      .map(([transicao, tempos]) => ({
        transicao,
        tempoMedio: Math.round(tempos.reduce((a, b) => a + b, 0) / tempos.length)
      }))
      .sort((a, b) => b.tempoMedio - a.tempoMedio)
      .slice(0, 10); // Top 10 transições
  };

  // Carregar dados específicos do funcionário
  const carregarDadosFuncionario = async () => {
    if (!funcionarioSelecionado || !filtros.dataInicio || !filtros.dataFim) {
      return;
    }
    
    try {
      const res = await api.relatorioLeituras({
        ...filtros,
        funcionario: funcionarioSelecionado.codigo
      });
      
      if (res.success) {
        const dados = res.dados || [];
        processarDadosFuncionario(dados);
      }
    } catch (error) {
      console.error('Erro ao carregar dados do funcionário:', error);
    }
  };

  // Processar dados do funcionário
  const processarDadosFuncionario = (dados) => {
    // Produção por dia
    const producaoPorDia = {};
    const temposEntreFormulas = [];
    
    dados.forEach(item => {
      const data = new Date(item.data).toISOString().split('T')[0];
      
      if (!producaoPorDia[data]) {
        producaoPorDia[data] = new Set();
      }
      producaoPorDia[data].add(item.codigo_requisicao);
    });
    
    const graficoProducao = Object.entries(producaoPorDia)
      .map(([data, requisicoes]) => ({
        data: new Date(data).toLocaleDateString('pt-BR'),
        requisicoes: requisicoes.size
      }))
      .sort((a, b) => new Date(a.data) - new Date(b.data));
    
    // Calcular tempo médio entre fórmulas
    const dadosOrdenados = dados.sort((a, b) => new Date(a.data) - new Date(b.data));
    const requisicoesUnicas = [...new Set(dados.map(d => d.codigo_requisicao))];
    
    requisicoesUnicas.forEach(codigo => {
      const reqDados = dadosOrdenados.filter(d => d.codigo_requisicao === codigo);
      
      for (let i = 0; i < reqDados.length - 1; i++) {
        const tempoMs = new Date(reqDados[i + 1].data) - new Date(reqDados[i].data);
        const tempoMin = Math.round(tempoMs / (1000 * 60));
        temposEntreFormulas.push({
          requisicao: codigo,
          tempo: tempoMin
        });
      }
    });
    
    setGraficos(prev => ({
      ...prev,
      producaoFuncionario: graficoProducao,
      tempoMedioFormulas: temposEntreFormulas.slice(0, 20) // Top 20
    }));
  };

  // Aplicar filtros
  const aplicarFiltros = async () => {
    // Verificar se pelo menos um filtro está preenchido
    if (!filtros.dataInicio && !filtros.dataFim && !filtros.setor && !filtros.funcionario) {
      setError('Por favor, selecione pelo menos um filtro');
      return;
    }
    
    // Se tiver apenas uma data, usar a mesma para início e fim
    let dataInicioFiltro = filtros.dataInicio;
    let dataFimFiltro = filtros.dataFim;
    
    if (filtros.dataInicio && !filtros.dataFim) {
      dataFimFiltro = filtros.dataInicio;
    } else if (!filtros.dataInicio && filtros.dataFim) {
      dataInicioFiltro = filtros.dataFim;
    }
    
    // Se não tiver nenhuma data, usar o mês atual
    if (!dataInicioFiltro && !dataFimFiltro) {
      const hoje = new Date();
      const primeiroDiaMes = new Date(hoje.getFullYear(), hoje.getMonth(), 1);
      const ultimoDiaMes = new Date(hoje.getFullYear(), hoje.getMonth() + 1, 0);
      
      dataInicioFiltro = primeiroDiaMes.toISOString().split('T')[0];
      dataFimFiltro = ultimoDiaMes.toISOString().split('T')[0];
    }
    
    try {
      setLoading(true);
      setError(null);
      
      // Carregar dados com filtros
      const filtrosCompletos = {
        dataInicio: dataInicioFiltro,
        dataFim: dataFimFiltro,
        setor: filtros.setor,
        funcionario: filtros.funcionario
      };
      
      const res = await api.relatorioLeituras(filtrosCompletos);
      
      if (res.success) {
        const dados = res.dados || [];
        
        // Processar dados filtrados
        processarDadosFiltrados(dados);
        
        // Se há funcionário selecionado, carregar dados específicos
        if (funcionarioSelecionado) {
          await carregarDadosFuncionario();
        }
      } else {
        setError(res.message || 'Erro ao carregar dados');
      }
    } catch (error) {
      console.error('Erro ao aplicar filtros:', error);
      setError('Erro ao carregar dados. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  // Processar dados filtrados
  const processarDadosFiltrados = (dados) => {
    const hoje = new Date().toISOString().split('T')[0];
    
    // Fórmulas por dia do período filtrado
    const formulasPorDia = {};
    const setoresRequisicoes = {};
    let formulasHoje = 0;
    
    dados.forEach(item => {
      const dataItem = new Date(item.data).toISOString().split('T')[0];
      
      // Agrupar por dia
      if (!formulasPorDia[dataItem]) {
        formulasPorDia[dataItem] = new Set();
      }
      formulasPorDia[dataItem].add(item.codigo_requisicao);
      
      // Contar fórmulas de hoje
      if (dataItem === hoje) {
        formulasHoje++;
      }
      
      // Agrupar por setor (total do período filtrado)
      const setor = item.setor_efetivo || item.setor_leitura || item.funcionario_setor || 'Sem Setor';
      if (!setoresRequisicoes[setor]) {
        setoresRequisicoes[setor] = new Set();
      }
      setoresRequisicoes[setor].add(item.codigo_requisicao);
    });
    
    // Converter para array do gráfico
    const graficoPorDia = Object.entries(formulasPorDia)
      .map(([data, requisicoes]) => ({
        data: new Date(data).toLocaleDateString('pt-BR', { 
          day: '2-digit', 
          month: '2-digit' 
        }),
        dataCompleta: data,
        formulas: requisicoes.size
      }))
      .sort((a, b) => new Date(a.dataCompleta) - new Date(b.dataCompleta));
      
    // Converter setores para cards (período filtrado)
    const setoresCards = Object.entries(setoresRequisicoes)
      .map(([setor, requisicoes]) => ({
        setor,
        quantidade: requisicoes.size
      }))
      .sort((a, b) => b.quantidade - a.quantidade);
    
    // Calcular tempo médio entre setores
    const tempoMedioSetores = calcularTempoMedioSetores(dados);
    
    setGraficos(prev => ({
      ...prev,
      formulasPorDia: graficoPorDia,
      tempoMedioSetores
    }));
    
    setCards({
      formulasHoje,
      setores: setoresCards,
      setoresHoje: [] // Vazio para dados filtrados
    });
  };

  // Abrir modal de setor
  const abrirModalSetor = async (nomeSetor) => {
    try {
      setModalSetorNome(nomeSetor);
      setModalSetorOpen(true);
      setModalSetorLoading(true);

      const hasRange = filtros.dataInicio && filtros.dataFim;
      const params = hasRange
        ? { nome: nomeSetor, dataInicio: filtros.dataInicio, dataFim: filtros.dataFim }
        : { nome: nomeSetor, periodo: 30 };

      const res = await api.setorFuncionarios(params);
      if (res.success) setModalSetorFuncionarios(res.dados || []);
      else setModalSetorFuncionarios([]);
    } catch (e) {
      console.error('Erro ao buscar funcionários do setor:', e);
      setModalSetorFuncionarios([]);
    } finally {
      setModalSetorLoading(false);
    }
  };

  // Effects
  useEffect(() => {
    carregarOpcoesFiltro();
    carregarDadosGerais();
    carregarRequisicoesHoje();
  }, [carregarOpcoesFiltro, carregarDadosGerais, carregarRequisicoesHoje]);

  // Atualização automática das requisições de hoje a cada 30 segundos
  useEffect(() => {
    const interval = setInterval(() => {
      carregarRequisicoesHoje();
    }, 30000); // 30 segundos

    return () => clearInterval(interval);
  }, [carregarRequisicoesHoje]);

  useEffect(() => {
    if (filtros.funcionario && opcoesFiltro.funcionarios.length > 0) {
      const funcionario = opcoesFiltro.funcionarios.find(f => f.codigo === filtros.funcionario);
      setFuncionarioSelecionado(funcionario);
    }
  }, [filtros.funcionario, opcoesFiltro.funcionarios]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box sx={{ flexGrow: 1 }}>
      <style>
        {`
          @keyframes blink {
            0%, 50% { opacity: 1; }
            51%, 100% { opacity: 0.3; }
          }
        `}
      </style>
      <Typography variant="h4" gutterBottom>
        Produtividade
      </Typography>
      
      {/* Filtros */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={2}>
            <TextField
              fullWidth
              label="Data Início"
              type="date"
              value={filtros.dataInicio}
              onChange={(e) => setFiltros({...filtros, dataInicio: e.target.value})}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              fullWidth
              label="Data Fim"
              type="date"
              value={filtros.dataFim}
              onChange={(e) => setFiltros({...filtros, dataFim: e.target.value})}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel>Setor</InputLabel>
              <Select
                value={filtros.setor}
                label="Setor"
                onChange={(e) => setFiltros({...filtros, setor: e.target.value})}
              >
                <MenuItem value="">Todos</MenuItem>
                {opcoesFiltro.setores.map(setor => (
                  <MenuItem key={setor.id} value={setor.nome}>
                    {setor.nome}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <Autocomplete
              options={opcoesFiltro.funcionarios}
              getOptionLabel={(option) => option.nome}
              value={funcionarioSelecionado}
              freeSolo
              onChange={(event, newValue) => {
                if (typeof newValue === 'string') {
                  // Usuário digitou um nome
                  setFuncionarioSelecionado(null);
                  setFiltros({...filtros, funcionario: newValue});
                } else {
                  // Usuário selecionou da lista
                  setFuncionarioSelecionado(newValue);
                  setFiltros({...filtros, funcionario: newValue ? (newValue.codigo || newValue.nome) : ''});
                }
              }}
              onInputChange={(event, newInputValue) => {
                // Atualizar o filtro conforme o usuário digita
                setFiltros({...filtros, funcionario: newInputValue});
              }}
              renderInput={(params) => (
                <TextField {...params} label="Funcionário" placeholder="Selecione um funcionário" />
              )}
              renderOption={(props, option) => (
                <li {...props}>
                  <Box>
                    <Typography variant="body1">{option.nome}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {option.setor}
                    </Typography>
                  </Box>
                </li>
              )}
            />
          </Grid>
          <Grid item xs={12} md={1}>
            <Button
              variant="contained"
              onClick={aplicarFiltros}
              disabled={!filtros.dataInicio && !filtros.dataFim && !filtros.setor && !filtros.funcionario}
            >
              Aplicar
            </Button>
          </Grid>
          <Grid item xs={12} md={1}>
            <Button
              variant="outlined"
              onClick={() => {
                setFiltros({
                  dataInicio: '',
                  dataFim: '',
                  setor: '',
                  funcionario: ''
                });
                setFuncionarioSelecionado(null);
                carregarDadosGerais();
              }}
            >
              Limpar
            </Button>
          </Grid>
        </Grid>
        
        {/* Indicador de Status */}
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {cards.setores.length > 0 
              ? `📊 Dados filtrados: ${filtros.dataInicio} até ${filtros.dataFim}${filtros.setor ? ` - Setor: ${filtros.setor}` : ''}${funcionarioSelecionado ? ` - Funcionário: ${funcionarioSelecionado.nome}` : ''}`
              : '📈 Exibindo dados de hoje'
            }
            {(cards.setores.length > 0 || cards.setoresHoje.length > 0) && (
              <span style={{ marginLeft: '10px' }}>
                • {cards.setores.length > 0 ? cards.setores.length : cards.setoresHoje.length} setores encontrados
              </span>
            )}
          </Typography>
        </Box>
      </Paper>

      {/* Cards de Estatísticas */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {/* Card de Requisições do Dia (Tempo Real) */}
        <Grid item xs={12} sm={6} md={4} lg={3}>
          <Card sx={{ border: '2px solid', borderColor: 'primary.main', position: 'relative' }}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2}>
                <TrendingUp color="primary" sx={{ fontSize: 40 }} />
                <Box>
                  <Typography variant="h3" color="primary" fontWeight="bold">
                    {requisicoesHoje}
                  </Typography>
                  <Typography variant="body1" color="text.primary" fontWeight="medium">
                    Total de Requisições Hoje
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Todos os setores
                  </Typography>
                  <Box display="flex" alignItems="center" gap={1} mt={1}>
                    <Typography variant="caption" color="text.secondary">
                      {ultimaAtualizacao && `Atualizado: ${ultimaAtualizacao.toLocaleTimeString()}`}
                    </Typography>
                    <Refresh 
                      sx={{ fontSize: 12, cursor: 'pointer', color: 'text.secondary' }}
                      onClick={() => carregarRequisicoesHoje()}
                      title="Atualizar agora"
                    />
                  </Box>
                </Box>
              </Box>
              {/* Indicador de tempo real */}
              <Box 
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  width: 8,
                  height: 8,
                  backgroundColor: 'success.main',
                  borderRadius: '50%',
                  animation: 'blink 2s infinite'
                }}
              />
            </CardContent>
          </Card>
        </Grid>



        {/* Card de Fórmulas do Dia */}
        <Grid item xs={12} sm={6} md={4} lg={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2}>
                <Today color="secondary" />
                <Box>
                  <Typography variant="h4" color="secondary">
                    {cards.formulasHoje}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Fórmulas Hoje
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Processadas
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Cards de Setores */}
        {(cards.setores.length > 0 ? cards.setores : cards.setoresHoje).map((setor, index) => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={setor.setor}>
            <Card onClick={() => abrirModalSetor(setor.setor)} sx={{ cursor: 'pointer' }}>
              <CardContent>
                <Box display="flex" alignItems="center" gap={2}>
                  <Business color="secondary" />
                  <Box>
                    <Typography variant="h4" color="secondary.main">
                      {setor.quantidade}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {setor.setor}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {cards.setores.length > 0 
                        ? `Período: ${filtros.dataInicio} - ${filtros.dataFim}`
                        : 'Hoje'
                      }
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Detalhamento por Setor - Requisições de Hoje */}
      {requisicoesHojePorSetor.length > 0 && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">
              📊 Detalhamento por Setor - Requisições de Hoje
            </Typography>
            <Button
              variant="outlined"
              size="small"
              onClick={() => carregarRequisicoesHoje()}
              startIcon={<Refresh />}
            >
              Atualizar
            </Button>
          </Box>
          <Grid container spacing={2}>
            {requisicoesHojePorSetor.map((setor, index) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={setor.setor}>
                <Card variant="outlined" sx={{ backgroundColor: index === 0 ? 'primary.light' : 'background.paper' }}>
                  <CardContent sx={{ py: 1 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Typography variant="body2" color={index === 0 ? 'primary.contrastText' : 'text.primary'}>
                        {setor.setor}
                      </Typography>
                      <Typography variant="h6" color={index === 0 ? 'primary.contrastText' : 'primary.main'} fontWeight="bold">
                        {setor.quantidade}
                      </Typography>
                    </Box>
                    <Typography variant="caption" color={index === 0 ? 'primary.contrastText' : 'text.secondary'}>
                      {((setor.quantidade / requisicoesHoje) * 100).toFixed(1)}% do total
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
          
          {/* Informações de Debug */}
          <Box sx={{ 
            mt: 2, 
            p: 1, 
            backgroundColor: integridadeContagem ? 'success.light' : 'warning.light', 
            borderRadius: 1,
            border: integridadeContagem ? '1px solid' : '2px solid',
            borderColor: integridadeContagem ? 'success.main' : 'warning.main'
          }}>
            <Typography variant="caption" color={integridadeContagem ? 'success.dark' : 'warning.dark'}>
              {integridadeContagem ? '✅' : '⚠️'} 
              {integridadeContagem ? 'Contagem verificada' : 'Possível inconsistência na contagem'} - 
              Total de requisições únicas = {requisicoesHoje} | 
              Soma dos setores = {requisicoesHojePorSetor.reduce((acc, setor) => acc + setor.quantidade, 0)} |
              Última atualização: {ultimaAtualizacao?.toLocaleString()}
            </Typography>
            {!integridadeContagem && (
              <Typography variant="caption" display="block" sx={{ mt: 0.5 }} color="warning.dark">
                💡 Dica: Verifique o console do navegador (F12) para mais detalhes sobre a análise
              </Typography>
            )}
          </Box>
        </Paper>
      )}

      {/* Gráficos Gerais */}
      <Grid container spacing={2} sx={{ mb: 3, mt: 4 }}>
        {/* Gráfico de Fórmulas por Dia */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">
                {cards.setores.length > 0 
                  ? `Requisições por Dia - Período Filtrado`
                  : 'Requisições por Dia - Mês Atual'
                }
              </Typography>
              {graficos.formulasPorDia.length > 0 && (
                <Box textAlign="right">
                  <Typography variant="caption" color="text.secondary">
                    Total: {graficos.formulasPorDia.reduce((acc, item) => acc + item.formulas, 0)} requisições
                  </Typography>
                  <br />
                  <Typography variant="caption" color="text.secondary">
                    Média: {Math.round(graficos.formulasPorDia.reduce((acc, item) => acc + item.formulas, 0) / graficos.formulasPorDia.length)} por dia
                  </Typography>
                </Box>
              )}
            </Box>
            {graficos.formulasPorDia.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={graficos.formulasPorDia}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="data" 
                    tick={{ fontSize: 12 }}
                    angle={-45}
                    textAnchor="end"
                    height={60}
                  />
                  <YAxis tick={{ fontSize: 12 }} />
                  <RechartsTooltip 
                    labelFormatter={(label) => `Data: ${label}`}
                    formatter={(value) => [value, 'Requisições']}
                    contentStyle={{
                      backgroundColor: '#f5f5f5',
                      border: '1px solid #ccc',
                      borderRadius: '4px'
                    }}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="formulas" 
                    stroke={CORES.primary} 
                    fill={`${CORES.primary}30`}
                    strokeWidth={2}
                  />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <Box 
                display="flex" 
                justifyContent="center" 
                alignItems="center" 
                height={300}
                color="text.secondary"
              >
                <Typography variant="body1">
                  Nenhum dado encontrado para o período selecionado
                </Typography>
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Gráfico de Tempo Médio entre Setores */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Tempo Médio entre Setores (minutos)
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={graficos.tempoMedioSetores}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="transicao" />
                <YAxis />
                <RechartsTooltip />
                <Bar dataKey="tempoMedio" fill={CORES.warning} />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
      </Grid>

      {/* Dados do Funcionário (quando selecionado) */}
      {funcionarioSelecionado && (
        <>
          <Divider sx={{ my: 4 }} />
          <Typography variant="h5" gutterBottom sx={{ mt: 2 }}>
            Produção de {funcionarioSelecionado.nome}
          </Typography>
          
          <Grid container spacing={2} sx={{ mt: 2 }}>
            {/* Gráfico de Produção por Dia */}
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Requisições por Dia
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={graficos.producaoFuncionario}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="data" />
                    <YAxis />
                    <RechartsTooltip />
                    <Line type="monotone" dataKey="requisicoes" stroke={CORES.success} strokeWidth={2} />
                  </LineChart>
                </ResponsiveContainer>
              </Paper>
            </Grid>

            {/* Gráfico de Tempo Médio entre Fórmulas */}
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Tempo entre Fórmulas (minutos)
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={graficos.tempoMedioFormulas}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="requisicao" />
                    <YAxis />
                    <RechartsTooltip />
                    <Bar dataKey="tempo" fill={CORES.info} />
                  </BarChart>
                </ResponsiveContainer>
              </Paper>
            </Grid>
          </Grid>
        </>
      )}

      {/* Modal: Funcionários do Setor */}
      <Dialog open={modalSetorOpen} onClose={() => setModalSetorOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Funcionários - {modalSetorNome}</DialogTitle>
        <DialogContent dividers>
          {modalSetorLoading ? (
            <Box display="flex" justifyContent="center" my={3}><CircularProgress /></Box>
          ) : (
            <List>
              {modalSetorFuncionarios.length === 0 && (
                <ListItem><ListItemText primary="Sem dados para o período" /></ListItem>
              )}
              {modalSetorFuncionarios.map((f) => (
                <ListItem key={f.codigo} divider>
                  <ListItemText
                    primary={`${f.nome} (${f.codigo})`}
                    secondary={`Requisições únicas: ${f.requisicoes_unicas} • Leituras: ${f.total_leituras} • Dias ativos: ${f.dias_ativos}`}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModalSetorOpen(false)}>Fechar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Produtividade;
